<template>
  <div class="app-notification" ref="notificationRef">    <button 
      @click="toggleNotificationPanel" 
      class="app-notification__btn" 
      :class="{ 'app-notification__btn--has-notifications': notificationCount > 0 }"
      :title="$t('notifications.title')"
    >
      <div class="app-notification__icon-wrapper">
        <svg class="app-notification__icon" viewBox="0 0 24 24" width="22" height="22">
          <path fill="currentColor" d="M12,2C13.1,2 14,2.9 14,4C14,4.1 14,4.2 13.9,4.3C16.3,5.2 18,7.5 18,10.1V15.5C18,16.8 18.6,18 19.6,18.7L20,19V20H4V19L4.4,18.7C5.4,18 6,16.8 6,15.5V10.1C6,7.5 7.7,5.2 10.1,4.3C10,4.2 10,4.1 10,4C10,2.9 10.9,2 12,2M10,21C10,22.1 10.9,23 12,23C13.1,23 14,22.1 14,21" />
        </svg>
        <div v-if="notificationCount > 0" class="app-notification__pulse"></div>
      </div>
      <span v-if="notificationCount > 0" class="app-notification__badge">
        {{ notificationCount > 9 ? '9+' : notificationCount }}
      </span>
    </button>
      <transition name="app-notification-fade">
      <div v-if="panelVisible" class="app-notification__backdrop" @click="closeNotificationPanel"></div>
    </transition>
    <transition name="app-notification-slide">
      <div v-if="panelVisible" class="app-notification__panel-wrapper">
        <NotificationPanel 
          @close="closeNotificationPanel"
          :notifications="userNotificationsList"
          :loading="loading"
          :error="error"
          @refresh="fetchNotifications"
        />
      </div>
    </transition>

    <!-- OTP Verification Dialog -->
    <OtpVerificationDialog
      :show="showOtpDialog"
      :notification="selectedNotification"
      :is-loading="otpLoading"
      @close="handleCloseOtpDialog"
      @verify="handleOtpVerification"
    />
  </div>
</template>

<script setup lang="ts">
/// <reference types="vue" />
// @ts-ignore
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from '@/composables/useStore'
import { useI18n } from 'vue-i18n'
import type { UserNotificationDTO } from '@/types'
import NotificationPanel from './NotificationPanel.vue'
import OtpVerificationDialog from './OtpVerificationDialog.vue'
import userInvitationsService from '@/services/userInvitations.service'
import userNotificationService from '@/services/userNotification.service'

// Define interfaces
interface NotificationItem {
  id: string | number
  type: string
  title: string
  message: string
  timestamp: Date
  read: boolean
  data?: any
  actionUrl?: string
  projectId?: number
  referenceId?: number
}

interface InvitationData {
  id: string | number
  projectId: string
  projectName: string
  inviterName: string
  inviterEmail: string
  accessRights: string[]
  invitationDate: string
  status: 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'EXPIRED'
  expirationDate?: string
}

// Store and i18n
const router = useRouter()
const store = useStore()
const { t } = useI18n()

// Reactive data
const notificationRef = ref<HTMLElement | null>(null)
const panelVisible = ref(false)
const notifications = ref<NotificationItem[]>([])
const loading = ref(false)
const error = ref<string | null>(null)

// OTP Dialog state
const showOtpDialog = ref(false)
const selectedNotification = ref<NotificationItem | null>(null)
const otpLoading = ref(false)

// Computed properties
const notificationCount = computed((): number => {
  return notifications.value.filter((n: NotificationItem) => !n.read).length
})

// Extract UserNotificationDTO items from notifications for the panel
const userNotificationsList = computed((): UserNotificationDTO[] => {
  return notifications.value
    .filter((n: NotificationItem) => n.data && typeof n.data === 'object' && 'contextData' in n.data)
    .map((n: NotificationItem) => n.data as UserNotificationDTO)
})

// Methods
const toggleNotificationPanel = (): void => {
  panelVisible.value = !panelVisible.value
  if (panelVisible.value) {
    fetchNotifications()
    document.addEventListener('click', handleClickOutside)
  } else {
    document.removeEventListener('click', handleClickOutside)
  }
}

const closeNotificationPanel = (): void => {
  panelVisible.value = false
  document.removeEventListener('click', handleClickOutside)
}

const handleClickOutside = (event: Event): void => {
  if (notificationRef.value && !notificationRef.value.contains(event.target as Node)) {
    closeNotificationPanel()
  }
}

const fetchNotifications = async (): Promise<void> => {
  loading.value = true
  error.value = null

  try {
    const allNotifications: NotificationItem[] = []
    
    // Only fetch project invitations for CLIENT users
    const currentUser = store.getters.getUser
    const shouldFetchInvitations = currentUser?.type === 'CLIENT'
    
    // Fetch project invitations (only for CLIENT users) and user notifications
    const [invitationsResponse, userNotifications] = await Promise.all([
      shouldFetchInvitations ? userInvitationsService.getPendingInvitations() : Promise.resolve(null),
      userNotificationService.getNotifications()
    ])

    // Process project invitations (only if fetched)
    if (invitationsResponse && shouldFetchInvitations) {
      let invitations: InvitationData[] = []
      if (Array.isArray(invitationsResponse)) {
        invitations = invitationsResponse as unknown as InvitationData[]
      } else if ((invitationsResponse as any).invitations && Array.isArray((invitationsResponse as any).invitations)) {
        invitations = (invitationsResponse as any).invitations as InvitationData[]
      } else if ((invitationsResponse as any).data && Array.isArray((invitationsResponse as any).data)) {
        invitations = (invitationsResponse as any).data as InvitationData[]
      }

      // Transform invitations into notification format
      const invitationNotifications = invitations.map((invitation: InvitationData): NotificationItem => ({
        id: invitation.id,
        type: 'project_invitation',
        title: t('notifications.projectInvitation'),
        message: t('notifications.invitedToProject', {
          projectName: invitation.projectName,
          firmName: invitation.inviterName
        }),
        timestamp: new Date(invitation.invitationDate),
        read: false,
        data: invitation
      }))

      allNotifications.push(...invitationNotifications)
    }

    // Process user notifications
    if (userNotifications && Array.isArray(userNotifications)) {
      const userNotifs = userNotifications.map((notif): NotificationItem => ({
        id: notif.id,
        type: notif.type,
        title: notif.type, // Use type as placeholder, will be translated in NotificationPanel
        message: notif.type, // Use type as placeholder, will be translated in NotificationPanel
        timestamp: new Date(notif.createdAt),
        read: notif.read,
        actionUrl: undefined, // Will be computed in NotificationPanel
        projectId: notif.projectId,
        referenceId: notif.referenceId,
        data: notif // IMPORTANT: Keep the original notification with contextData
      }))

      allNotifications.push(...userNotifs)
    }

    // Sort all notifications by timestamp (newest first)
    allNotifications.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())

    notifications.value = allNotifications

  } catch (err: any) {
    console.error('Error fetching notifications:', err)
    error.value = t('notifications.loadError')
  } finally {
    loading.value = false
  }
}

const handleShowOtpDialog = (notification: NotificationItem): void => {
  selectedNotification.value = notification
  showOtpDialog.value = true
  // Close the notification panel
  closeNotificationPanel()
}

const handleCloseOtpDialog = (): void => {
  showOtpDialog.value = false
  selectedNotification.value = null
  otpLoading.value = false
}

const handleOtpVerification = async (data: { notification: NotificationItem; otp: string }): Promise<void> => {
  otpLoading.value = true

  try {
    const invitation = data.notification.data

    await userInvitationsService.acceptInvitation(invitation.id, data.otp)

    // Remove notification from list
    notifications.value = notifications.value.filter((n: NotificationItem) => n.id !== data.notification.id)

    // Close OTP dialog
    handleCloseOtpDialog()

    // Show success message
    store.commit('SET_SUCCESS', t('notifications.invitationAccepted'))

    // Optionally refresh user data
    store.dispatch('fetchUser')

  } catch (err: any) {
    console.error('Error accepting invitation with OTP:', err)

    let errorMessage = t('notifications.acceptError')
    if (err.response?.data?.message) {
      errorMessage = err.response.data.message
    } else if (err.message) {
      errorMessage = err.message
    }

    store.commit('SET_ERROR', errorMessage)
  } finally {
    otpLoading.value = false
  }
}

const handleDeclineInvitation = async (notification: NotificationItem): Promise<void> => {
  try {
    const invitation = notification.data
    await userInvitationsService.declineInvitation(invitation.id)

    // Remove notification from list
    notifications.value = notifications.value.filter((n: NotificationItem) => n.id !== notification.id)

    // Show success message
    store.commit('SET_SUCCESS', t('notifications.invitationDeclined'))

  } catch (err: any) {
    console.error('Error declining invitation:', err)
    store.commit('SET_ERROR', t('notifications.declineError'))
  }
}

const handleNavigateToAction = async (notification: NotificationItem): Promise<void> => {
  try {
    // Navigate to the action URL
    if (notification.actionUrl) {
      await router.push(notification.actionUrl)
      // Close the notification panel
      closeNotificationPanel()
      
      // Mark as read
      if (!notification.read) {
        await userNotificationService.markAsRead(notification.id as number)
        // Update local state
        const index = notifications.value.findIndex((n: NotificationItem) => n.id === notification.id)
        if (index !== -1) {
          notifications.value[index].read = true
        }
      }
    }
  } catch (err: any) {
    console.error('Error navigating to action:', err)
    store.commit('SET_ERROR', t('notifications.navigationError'))
  }
}

const handleMarkAsRead = async (notification: NotificationItem): Promise<void> => {
  try {
    if (!notification.read) {
      await userNotificationService.markAsRead(notification.id as number)
      
      // Update local state
      const index = notifications.value.findIndex((n: NotificationItem) => n.id === notification.id)
      if (index !== -1) {
        notifications.value[index].read = true
      }
      
      store.commit('SET_SUCCESS', t('notifications.markedAsRead'))
    }
  } catch (err: any) {
    console.error('Error marking notification as read:', err)
    store.commit('SET_ERROR', t('notifications.markReadError'))
  }
}

// Lifecycle hooks
onMounted(() => {
  fetchNotifications()
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.app-notification {
  position: relative;
  margin-right: var(--space-md);
}

.app-notification__btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: transparent;
  border: none;
  border-radius: var(--radius-full);
  color: white;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.app-notification__btn:hover {
  background: transparent;
  transform: translateY(-1px);
}

.app-notification__btn:active {
  transform: translateY(0);
  transition: transform 0.1s ease;
}

.app-notification__btn--has-notifications {
  animation: subtle-glow 3s ease-in-out infinite;
}

.app-notification__icon-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.app-notification__icon {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
}

.app-notification__btn:hover .app-notification__icon {
  transform: scale(1.1) rotate(5deg);
}

.app-notification__pulse {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  height: 100%;
  background: transparent;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  animation: notification-pulse 2s ease-in-out infinite;
  pointer-events: none;
}

.app-notification__badge {
  position: absolute;
  top: -6px;
  right: -6px;
  background: linear-gradient(135deg, #ff6b6b, #ee5a52);
  color: white;
  border-radius: 12px;
  min-width: 20px;
  height: 20px;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  padding: 0 6px;  border: 2px solid white;
  animation: badge-bounce 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.app-notification__badge:before {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(135deg, rgba(255, 107, 107, 0.4), rgba(238, 90, 82, 0.4));
  border-radius: 14px;
  z-index: -1;
  animation: badge-glow 2s ease-in-out infinite alternate;
}

.app-notification__backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  /* Ensure backdrop is never fully transparent */
}

.app-notification__panel-wrapper {
  position: absolute;
  top: calc(100% + 16px);
  right: 0;
  z-index: 1001;
  width: 420px;  max-width: 95vw;
  transform-origin: top right;
}

/* Enhanced Animations */
@keyframes subtle-glow {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.9;
  }
}

@keyframes notification-pulse {
  0%, 100% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.8);
  }
  50% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1.2);
  }
}

@keyframes badge-bounce {
  0% {
    transform: scale(0.3);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes badge-glow {
  0% {
    opacity: 0.6;
  }
  100% {
    opacity: 0.9;
  }
}

.app-notification-fade-enter-active,
.app-notification-fade-leave-active {
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.app-notification-fade-enter-from,
.app-notification-fade-leave-to {
  opacity: 0;
}

.app-notification-slide-enter-active,
.app-notification-slide-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: top right;
}

.app-notification-slide-enter-from,
.app-notification-slide-leave-to {
  opacity: 0;
  transform: scale(0.9) translateY(-15px) translateX(10px);
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .app-notification__btn {
    width: 40px;
    height: 40px;
  }
  
  .app-notification__panel-wrapper {
    position: fixed;
    top: 80px;
    left: 12px;
    right: 12px;
    width: auto;
    max-width: none;
    transform-origin: top center;
  }
  
  .app-notification__badge {
    top: -4px;
    right: -4px;
    min-width: 18px;
    height: 18px;
    font-size: 10px;
  }
}

/* Enhanced z-index management */
.app-notification__backdrop {
  z-index: 1000;
}

.app-notification__panel-wrapper {
  z-index: 1001;
}

/* Improved mobile positioning */
@media (max-width: 480px) {
  .app-notification__panel-wrapper {
    top: 70px;
    left: 8px;
    right: 8px;
    transform-origin: top center;
  }
}

/* Better tablet positioning */
@media (min-width: 481px) and (max-width: 768px) {
  .app-notification__panel-wrapper {
    width: 380px;
    max-width: calc(100vw - 32px);
  }
}

/* Large screen optimization */
@media (min-width: 1200px) {
  .app-notification__panel-wrapper {
    width: 450px;
  }
}

/* Dark mode enhancements */
@media (prefers-color-scheme: dark) {
  .app-notification__btn {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.12), rgba(255, 255, 255, 0.04));
    border-color: rgba(255, 255, 255, 0.08);
  }
  
  .app-notification__btn:hover {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.08));
    border-color: rgba(255, 255, 255, 0.15);
  }
}

/* Reduced motion accessibility */
@media (prefers-reduced-motion: reduce) {
  .app-notification__btn,
  .app-notification__icon,
  .app-notification__badge {
    animation: none;
    transition: none;
  }
  
  .app-notification__pulse {
    display: none;
  }
  
  .app-notification__btn:hover .app-notification__icon {
    transform: none;
  }
}
</style>
